import React, { useState } from 'react'
import { useAtomValue } from 'jotai'
import { userAtom } from '../../store/userAtoms'
import { useTar } from '@/modules/i18n'
import { useOrganization } from '@/hooks/useOrganization'
import { usePartnerDashboardRows } from '@/hooks/usePartnerDashboardRows'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { Card, CardContent, Skeleton } from '@duck/ui'
import { NoDataEmptyState } from '../EmptyStates'
import { ClipboardDocumentListIcon, UserGroupIcon } from '@heroicons/react/24/outline'
import { usePartnerTar } from '@/hooks/usePartnerTar'
import { PartnerDashboardTable } from './PartnerDashboardTable'
import { CohortsOverviewTable } from './CohortsOverviewTable'
import { OrgWordmark } from '@/components/OrgWordmark'
import { CreateCohortDialog } from '../PartnerCohorts/CreateCohortDialog'

interface PartnerDashboardProps {
  organizationId?: string
}

export const PartnerDashboard: React.FC<PartnerDashboardProps> = ({ organizationId }) => {
  const user = useAtomValue(userAtom)
  const { t } = useTar()
  const { tP } = usePartnerTar()
  const effectiveOrgId = organizationId ?? user?.organizationId
  const { organization, isLoading: isOrgLoading } = useOrganization(effectiveOrgId)

  // Rendered both as the admin org-assignments page (organizationId passed) and
  // as a partner's own dashboard (no organizationId) — title/area follow suit.
  useDocumentTitle(
    organizationId ? organization?.name ?? organizationId : 'Dashboard',
    organizationId ? 'admin' : 'partner',
  )
  const { cohortsWithStats, flatRows, persons, isLoading: isLoadingRows } =
    usePartnerDashboardRows(organizationId)

  const isLoading = isLoadingRows || isOrgLoading
  const hasNoAssignments = flatRows.length === 0
  const hasNoCohorts = cohortsWithStats.length === 0
  const isViewingOwnDashboard = !organizationId
  const [isCreateCohortDialogOpen, setIsCreateCohortDialogOpen] = useState(false)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            {organizationId && organization?.name
              ? `${organization.name} – ${t('partner.dashboard')}`
              : t('partner.dashboard')}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {organizationId
              ? t('partner.dashboardDescriptionOrg')
              : t('partner.dashboardDescription', {
                firstName: user?.firstName || '',
                lastName: user?.lastName || ''
              })}
          </p>
        </div>
        {organization?.logoURL && (
          <OrgWordmark
            org={organization}
            className="max-h-12 max-w-48 m-0"
          />
        )}
      </div>

      {isLoading ? (
        <div className="space-y-6">
          <div className="overflow-x-auto">
            <div className="min-w-full border border-border rounded-md divide-y divide-border">
              <div className="bg-muted/15 px-6 py-3">
                <div className="flex items-center">
                  <div className="flex gap-6">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                </div>
              </div>

              <div className="bg-card divide-y divide-border">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div
                    key={index}
                    className="flex items-center px-6 py-4 gap-6"
                  >
                    <div className="flex items-center gap-4 min-w-[220px]">
                      <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center">
                        <Skeleton className="h-5 w-5 rounded-full" />
                      </div>
                      <div className="space-y-1">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                    </div>

                    <div className="flex-1 grid grid-cols-3 gap-6">
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-6 w-24 rounded-full" />
                      <Skeleton className="h-6 w-16 rounded-full" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {hasNoCohorts ? (
            <Card>
              <CardContent className="pt-6">
                <NoDataEmptyState
                  icon={UserGroupIcon}
                  title={tP('noCohortsYet')}
                  description={tP('noCohortsDescription')}
                  buttonText={isViewingOwnDashboard ? tP('createCohort') : undefined}
                  onButtonClick={
                    isViewingOwnDashboard
                      ? () => setIsCreateCohortDialogOpen(true)
                      : undefined
                  }
                />
              </CardContent>
            </Card>
          ) : (
            <>
              <CohortsOverviewTable
                cohortsWithStats={cohortsWithStats}
                organizationId={organizationId}
              />
              {hasNoAssignments ? (
                <Card>
                  <CardContent className="pt-6">
                    <NoDataEmptyState
                      icon={ClipboardDocumentListIcon}
                      title={t('partner.noAssignmentsYet')}
                      description={tP('noAssignmentsDescription')}
                    />
                  </CardContent>
                </Card>
              ) : (
                <PartnerDashboardTable rows={flatRows} persons={persons} />
              )}
            </>
          )}
        </div>
      )}

      {isViewingOwnDashboard && (
        <CreateCohortDialog
          isOpen={isCreateCohortDialogOpen}
          onClose={() => setIsCreateCohortDialogOpen(false)}
        />
      )}
    </div>
  )
}
